import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import PageLayout from 'prefabsComponents/LayoutContainers/PageLayout';

function IllustrationLayout({
  color,
  header,
  title,
  description,
  illustration,
  topImage, // Nueva prop para la imagen encima del título
  children,
}) {
  const [news, setNews] = useState([]);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [images, setImages] = useState(
    illustration.images && illustration.images.length
      ? illustration.images
      : [illustration.image || '']
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [nextImageIndex, setNextImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimer = useRef(null);

  // Precalcular siguiente índice
  useEffect(() => {
    setNextImageIndex((currentImageIndex + 1) % images.length);
  }, [currentImageIndex, images.length]);

  // Obtener y parsear noticias
  useEffect(() => {
    fetch('https://www.mundomaritimo.cl/main/sitemap_gnews')
      .then(response => response.text())
      .then(str => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(str, 'text/xml');
        const newsArray = Array.from(xmlDoc.getElementsByTagName('url')).map(urlElem => ({
          loc: urlElem.getElementsByTagName('loc')[0]?.textContent,
          pubDate: urlElem.getElementsByTagName('n:publication_date')[0]?.textContent,
          newsTitle: urlElem.getElementsByTagName('n:title')[0]?.textContent,
          newsDescription: urlElem.getElementsByTagName('n:genres')[0]?.textContent || '',
        }));
        setNews(newsArray);
      })
      .catch(console.error);
  }, []);

  // Cambio automático de noticias
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentNewsIndex(prev => (news.length ? (prev + 1) % news.length : 0));
    }, 3000);
    return () => clearInterval(interval);
  }, [news]);

  // Transición perfecta de imágenes
  useEffect(() => {
    if (images.length <= 1) return;

    const startTransition = () => {
      setIsTransitioning(true);
      transitionTimer.current = setTimeout(() => {
        setCurrentImageIndex(nextImageIndex);
        setIsTransitioning(false);
      }, 1000);
    };

    const interval = setInterval(startTransition, 1500);
    return () => {
      clearInterval(interval);
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
    };
  }, [images.length, nextImageIndex]);

  const currentNews = news[currentNewsIndex];

  return (
    <PageLayout background="white">
      <Grid container justifyContent="space-between">
        {/* Formulario */}
        <Grid item xs={12} sm={10} md={6} lg={5} xl={4} sx={{ mx: 'auto' }}>
          <SoftBox display="flex" flexDirection="column" justifyContent="start" height="100vh">
            <SoftBox >
              {!header ? (
                <>
                  {/* Imagen encima del título */}
                  {topImage && (
                    <SoftBox textAlign="center">
                      <SoftBox
                        component="img"
                        src={topImage}
                        alt="Encabezado"
                        width="60%"
                        borderRadius="8px"
                      />
                    </SoftBox>
                  )}
                </>
              ) : (
                header
              )}
            </SoftBox>
            <SoftBox px={3}>{children}</SoftBox>
          </SoftBox>
        </Grid>

        {/* Carrusel de imágenes */}
        <Grid item xs={12} lg={7} sx={{ display: { xs: 'none', lg: 'block' } }}>
          <SoftBox
            position="relative"
            width="100%"
            height="calc(100vh - 2rem)"
            borderRadius="16px"
            sx={{ p: 2, overflow: 'hidden' }}
          >
            <SoftBox width="100%" height="100%" position="relative" borderRadius="12px" overflow="hidden">
              {/* Imagen actual */}
              <SoftBox
                width="100%"
                height="100%"
                sx={{
                  position: 'absolute',
                  backgroundImage: `url(${images[currentImageIndex]})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  zIndex: 1,
                }}
              />

              {/* Imagen siguiente */}
              {isTransitioning && (
                <SoftBox
                  width="100%"
                  height="100%"
                  sx={{
                    position: 'absolute',
                    backgroundImage: `url(${images[nextImageIndex]})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    zIndex: 2,
                    animation: 'fadeIn 1s ease-in-out forwards',
                    '@keyframes fadeIn': {
                      '0%': { opacity: 0 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
              )}
            </SoftBox>

            {/* Overlays... */}
          </SoftBox>
        </Grid>
      </Grid>
    </PageLayout>
  );
}

IllustrationLayout.defaultProps = {
  color: 'info',
  header: '',
  title: '',
  description: '',
  illustration: {},
  topImage: '', // Valor predeterminado
};

IllustrationLayout.propTypes = {
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
    'dark',
  ]),
  header: PropTypes.node,
  title: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.node.isRequired,
  illustration: PropTypes.shape({
    image: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    description: PropTypes.string,
  }),
  topImage: PropTypes.string, // Nueva prop para la imagen
};

export default IllustrationLayout;
